import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { KeyRound, Plug, CheckCircle2, XCircle, Trash2 } from "lucide-react";

const inputCls = "mt-1 w-full rounded-lg border border-[#E5E5E0] px-3 py-2 text-sm bg-white";
const labelCls = "font-mono text-[10px] uppercase tracking-widest text-[#525252]";
const MODELS = [["gpt-5.5", "GPT-5.5 · calidad alta"], ["gpt-5.4", "GPT-5.4"], ["gpt-5.4-mini", "GPT-5.4 mini · rápido y económico"]];

/** Iter 156 — Idioma por defecto + traducción automática + OpenAI. La API key sólo viaja al backend. */
export default function TranslationSettingsPanel({ settings, languages, onChanged }) {
  const [apiKey, setApiKey] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const active = languages.filter((l) => l.active);

  const patch = async (p) => {
    try { await api.put("/languages/settings", p); onChanged(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const saveKey = async () => {
    try { await api.put("/languages/openai-key", { api_key: apiKey }); setApiKey(""); toast.success("API Key guardada en el servidor"); onChanged(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const clearKey = async () => {
    if (!window.confirm("¿Eliminar la API Key de OpenAI?")) return;
    try { await api.delete("/languages/openai-key"); onChanged(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post("/languages/test-connection");
      setTestResult({ ok: true, ...r.data }); toast.success("Conexión OpenAI correcta");
    } catch (err) { setTestResult({ ok: false, message: formatApiError(err) }); }
    finally { setTesting(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <section className="ds-card p-5 space-y-4" data-testid="translation-settings">
        <h3 className="font-display font-bold text-lg">Idioma por defecto y traducción</h3>
        <div>
          <label className={labelCls}>Idioma por defecto para nuevos clientes</label>
          <select value={settings.default_language_code || ""} onChange={(e) => patch({ default_language_code: e.target.value })} className={inputCls} data-testid="default-language-select">
            {active.map((l) => <option key={l.code} value={l.code}>{l.name} ({l.code})</option>)}
          </select>
          <div className="mt-1 text-[10px] text-[#525252]">Si un cliente no tiene idioma asignado se usa este. Idioma maestro: <span className="font-mono">{settings.master_language_code}</span>.</div>
        </div>
        <label className="flex items-start gap-3 text-sm cursor-pointer">
          <input type="checkbox" checked={!!settings.translation_enabled} onChange={(e) => patch({ translation_enabled: e.target.checked })} className="mt-0.5 accent-[#6E8B3D]" data-testid="translation-enabled-toggle" />
          <span><span className="font-semibold">Traducción automática activada</span><br /><span className="text-xs text-[#525252]">Proveedor: OpenAI. Si está desactivada no se realizan llamadas al proveedor.</span></span>
        </label>
        <label className="flex items-start gap-3 text-sm cursor-pointer">
          <input type="checkbox" checked={!!settings.auto_translate_on_save} onChange={(e) => patch({ auto_translate_on_save: e.target.checked })} className="mt-0.5 accent-[#6E8B3D]" data-testid="auto-translate-toggle" />
          <span><span className="font-semibold">Traducir automáticamente al guardar contenido maestro</span><br /><span className="text-xs text-[#525252]">Si está desactivado, las traducciones se marcan como DESACTUALIZADAS y se actualizan manualmente. Las traducciones revisadas nunca se sobrescriben sin confirmación.</span></span>
        </label>
        <div>
          <label className={labelCls}>Modelo</label>
          <select value={settings.openai_model || "gpt-5.5"} onChange={(e) => patch({ openai_model: e.target.value })} className={inputCls} data-testid="openai-model-select">
            {MODELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </section>

      <section className="ds-card p-5 space-y-4" data-testid="openai-settings">
        <div className="flex items-center gap-2"><KeyRound size={16} className="text-[#6E8B3D]" /><h3 className="font-display font-bold text-lg">Conexión OpenAI</h3></div>
        <div className="flex items-center gap-2 text-sm" data-testid="openai-key-status">
          {settings.has_api_key ? <><CheckCircle2 size={15} className="text-[#3F5222]" /> API Key configurada <span className="font-mono text-xs text-[#525252]">({settings.api_key_hint})</span>
            <button onClick={clearKey} className="ml-auto p-1.5 hover:bg-red-50 text-red-700 rounded" title="Eliminar clave" data-testid="openai-key-clear"><Trash2 size={13} /></button></>
            : <><XCircle size={15} className="text-[#9B2C1F]" /> Sin API Key</>}
        </div>
        <div>
          <label className={labelCls}>{settings.has_api_key ? "Sustituir API Key" : "API Key de OpenAI"}</label>
          <div className="flex gap-2 mt-1">
            <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="sk-…" autoComplete="off" className="flex-1 rounded-lg border border-[#E5E5E0] px-3 py-2 text-sm font-mono" data-testid="openai-key-input" />
            <button onClick={saveKey} disabled={apiKey.trim().length < 20} className="ds-btn primary" data-testid="openai-key-save">Guardar</button>
          </div>
          <div className="mt-1 text-[10px] text-[#525252]">Se guarda cifrada en el servidor y nunca vuelve al navegador ni a los logs.</div>
        </div>
        <div>
          <button onClick={testConnection} disabled={testing || !settings.has_api_key} className="ds-btn" data-testid="openai-test-btn">
            <Plug size={14} /> {testing ? "Probando…" : "Probar conexión"}
          </button>
          {testResult && (
            <div className={`mt-3 rounded-lg border p-3 text-sm ${testResult.ok ? "bg-[#F0F4E8] border-[#6E8B3D] text-[#3F5222]" : "bg-[#FDEDE7] border-[#E9A596] text-[#9B2C1F]"}`} data-testid="openai-test-result">
              {testResult.ok ? "✓ Conexión OpenAI correcta" : `✗ ${testResult.message}`}
              {testResult.checks?.length > 0 && (
                <div className="mt-1 font-mono text-[11px] opacity-80">{testResult.checks.map((c) => `Hola → ${c.target}: ${c.result || "—"}`).join(" · ")}</div>
              )}
            </div>
          )}
          {settings.last_connection_test && !testResult && (
            <div className="mt-2 text-[10px] text-[#525252]">Última prueba: {settings.last_connection_test.ok ? "correcta" : "con error"} · {new Date(settings.last_connection_test.at).toLocaleString("es-ES")}</div>
          )}
        </div>
      </section>
    </div>
  );
}
